import React, { createContext, useContext, useEffect, useState } from "react";
import Connector from "../sockets/SignalRConnecter";
import { LoadingScreenComponent } from "../presentation/components/state/Loading";
import { User } from "../core/entities/user";
import { useAuthHttpClient } from "../api/api-hooks/useAuthHttpClient";
import { roles } from "../core/const/roles";
import { ErrorPageScreen } from "../presentation/screens/public/ErrorPage/ErrorPage.Screen";

interface UserContextProps {
    user: User | null;
    isSuperAdmin: boolean;
    isMunicipalAdmin: boolean;
    isCaseWorker: boolean;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

interface UserProps {
    children: React.ReactNode;
}

export function UserProvider({ children }: UserProps): React.ReactElement {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [isMunicipalAdmin, setIsMunicipalAdmin] = useState(false);
    const [isCaseWorker, setIsCaseWorker] = useState(false);
    const [error, setError] = useState<any>();
    const { profile, signOut } = useAuthHttpClient();
    let connection: Connector | null = null;
    if (user) {
        connection = Connector.getInstance();
    }

    useEffect(() => {
        if (connection && user) {
            connection.events((s) => signOut());
        }
    }, [connection, signOut, user]);

    useEffect(() => {
        if (!user && !error) {
            profile()
                .then((user) => {
                    setUser(user);
                    if (user) {
                        setIsSuperAdmin(
                            user.roles.includes(roles.SuperAdminRole)
                        );
                        setIsMunicipalAdmin(
                            user.roles.includes(roles.MunicipalAdminRole)
                        );
                        setIsCaseWorker(
                            user.roles.includes(roles.CaseWorkerRole)
                        );
                        setIsLoading(false);
                    }
                })
                .catch(setError);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, user]);

    return (
        <UserContext.Provider
            value={{
                user,
                isSuperAdmin,
                isMunicipalAdmin,
                isCaseWorker
            }}
        >
            {isLoading && !error ? (
                <LoadingScreenComponent />
            ) : error ? (
                <ErrorPageScreen error={error} />
            ) : (
                children
            )}
        </UserContext.Provider>
    );
}

export function useAuthenticationContext(): UserContextProps {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error("useUserContext must be used within a UserProvider");
    }
    return context;
}
